import { RefObject, useEffect, useState } from 'react';

type SelectOption = {
  el: HTMLElement; // DOM Node
  id: string;
};

type SelectOpenTracking = {
  optionsEl: RefObject<HTMLElement>;
  selectedOptions: string[];
  triggerEl: RefObject<HTMLButtonElement>;
  shouldFocusOnFirstElementByDefault?: boolean;
  autoFocusOnInput: boolean;
};

type IsOpenTuple = [boolean, (isOpen: boolean) => void];

export function useSelectOpenTracking({
  optionsEl,
  selectedOptions = [],
  triggerEl,
  shouldFocusOnFirstElementByDefault = true,
  autoFocusOnInput = false,
}: SelectOpenTracking): IsOpenTuple {
  const [isOpen, setIsOpen] = useState(false);

  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role
  // when a listbox is opened focus on the currently selected item
  // or focus on the first available option and optionally select it
  useEffect(() => {
    if (isOpen) {
      /**
       because the option list can be a dynamically built nested structure
       we need to punch through the structure and only get the options
       DOM querying is simpler here and avoids needing to know anything about
       other component's state
       */
      const validOptions =
        optionsEl.current?.querySelectorAll('input[type="text"], [role="option"]') || [];
      const items: SelectOption[] = Array.from(validOptions).map((option) => ({
        el: option as unknown as HTMLElement,
        id: option.id,
      }));

      const matchedSelection = items.find((item) => selectedOptions.includes(item.id));

      // if autoFocusOnInput is true, allows focus to be on input and not selected item
      if (matchedSelection && !autoFocusOnInput) {
        matchedSelection.el?.focus();
      } else if (items[0] && shouldFocusOnFirstElementByDefault) {
        items[0].el?.focus();
      }
    }
  }, [isOpen, optionsEl, triggerEl]);

  return [isOpen, setIsOpen];
}
